import axios from 'axios'

import { getEnviron } from '@services/utils'
import { msalInstance } from '@features/config/auth'

import Config from '@features/config.json'

class MsApiAbstract {
    static mockerUrl = `${Config[getEnviron()]?.mocker?.protocol}://${Config[getEnviron()]?.mocker?.api}`
    static baseEndpoint = Config[getEnviron()]?.endpoints

    static getBaseUrl(mode, type) {
        return this.baseEndpoint[mode][type]
    }

    static getUrl(uri, mode, type, useMocker = false) {
        return useMocker ? `${this.mockerUrl}${uri}` : `${this.getBaseUrl(mode, type)}${uri}`
    }

    static async getAccessToken(scopes = []) {
        const currentAccount = msalInstance.getAllAccounts()[0]
        if (!currentAccount) throw new Error('No users logged in')

        try {
            const response = await msalInstance.acquireTokenSilent({
                scopes,
                account: currentAccount,
                forceRefresh: true
            })
            return response.accessToken
        } catch (error) {
            if (error.name === 'InteractionRequiredAuthError')
                return { message: error.message, isError: true }
            throw error
        }
    }

    static async getHeaders(scopes = [], headers = {}) {
        const accessToken = await this.getAccessToken(scopes)

        if (!accessToken) return { message: 'Error retrieving accessToken!' }
        if (accessToken?.isError) return { message: accessToken.message }

        return {
            headers: {
                ...headers,
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        }
    }

    static async prepareRequest(mode, type, scopes, headers, callback, execCallback) {
        if (mode === 'azure') scopes = [`${this.getBaseUrl(mode, type)}${scopes}`]
        else scopes = [`${this.getBaseUrl('azure', 'lzManager')}/user_impersonation`]

        const headers_ = await this.getHeaders(scopes, headers)

        if (headers_?.message) return this.handleError({message: headers_.message}, callback, execCallback)

        return { error: false, headers: headers_ }
    }

    static processResponse(result, callback, execCallback, okStatus) {
        if (result.status === okStatus) {
            const res = { except: false, error: false, data: result.data, callback }

            if (execCallback && callback?.success && typeof callback.success === 'function')
                callback.success(res)

            return res
        }

        const res = { error: true, data: result, callback }

        if (execCallback && callback?.error && typeof callback.error === 'function')
            callback.error(res)

        return res
    }

    static handleError(e, callback, execCallback) {
        const res = { except: true, data: e, callback }

        if (execCallback && callback?.error && typeof callback.error === 'function')
            callback.error(res)

        return res
    }

    static async request(method, api, data, options) {
        const {
            mode = 'azure',
            type = 'lzManager',
            scopes = null,
            headers = {},
            callback = {},
            execCallback = true,
            okStatus = method === 'post' ? 201 : 200,
            useMocker = false
        } = options

        try {
            const prepared = await this.prepareRequest(mode, type, scopes, headers, callback, execCallback)

            if (prepared?.except) return prepared

            const url = this.getUrl(api, mode, type, useMocker)
            let result

            switch (method) {
                case 'get':
                    result = axios.get(url, prepared.headers) ; break
                case 'post':
                    result = axios.post(url, data, prepared.headers) ; break
                case 'put':
                    result = axios.put(url, data, prepared.headers) ; break
                case 'patch':
                    result = axios.patch(url, data, prepared.headers) ; break
                case 'delete':
                    result = axios.delete(url, prepared.headers) ; break
                default:
                    throw new Error(`Unsupported method: ${method}`)
            }

            return result
                .then(result => { return this.processResponse(result, callback, execCallback, okStatus) })
                .catch(e => { return this.handleError(e, callback, execCallback) })

        } catch (e) {
            return this.handleError(e, callback, execCallback)
        }
    }

    static async getProcess(api = '', options = {}) {
        return this.request('get', api, null, options)
    }

    static async postProcess(api = '', data = {}, options = {}) {
        return this.request('post', api, data, options)
    }

    static async putProcess(api = '', data = {}, options = {}) {
        return this.request('put', api, data, options)
    }

    static async patchProcess(api = '', data = {}, options = {}) {
        return this.request('patch', api, data, options)
    }

    static async deleteProcess(api = '', options = {}) {
        return this.request('delete', api, null, options)
    }
}

export default MsApiAbstract